import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  getChartLegendItems,
} from '@/components/shadcn/charts'
import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from 'recharts'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { ChartLineDataSchema, ChartLineUISchema } from './types'

export function ChartLineContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: ChartLineUISchema
  dataSchema: ChartLineDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, emphasizeHeader, chartConfig } = uiSchema
  const {
    config,
    categoryKey,
    lineDataKeys,
    hideXAxis,
    hideYAxis,
    showDots = true,
    showGrid = true,
    showLegend = true,
    xAxisOrientation,
    yAxisOrientation,
    domain,
    syncId,
    referenceLines,
  } = chartConfig
  const { data } = dataSchema
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div
        className={clsx({
          'pt-5': hasHeader,
        })}
        style={{ height: contentHeight }}
      >
        <ChartContainer
          className="mx-auto h-full w-full"
          config={config}
          legend={showLegend ? <ChartLegendContent items={getChartLegendItems(config, lineDataKeys)} /> : undefined}
        >
          <LineChart data={data} syncId={syncId}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} />}
            <XAxis dataKey={categoryKey} hide={hideXAxis} orientation={xAxisOrientation} />
            <YAxis domain={domain} hide={hideYAxis} orientation={yAxisOrientation} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
            {lineDataKeys.map((key) => {
              const lineConfig = config[key as keyof typeof config]
              return (
                <Line
                  key={key}
                  type={lineConfig?.curveType ?? 'monotone'}
                  dataKey={key}
                  stroke={lineConfig?.color}
                  strokeWidth={lineConfig?.strokeWidth ?? 2}
                  dot={lineConfig?.dot ?? showDots}
                  activeDot={lineConfig?.activeDot ?? showDots}
                  connectNulls={lineConfig?.connectNulls ?? false}
                />
              )
            })}
            {referenceLines?.map((referenceLine, index) => (
              <ReferenceLine
                key={`${referenceLine.label || 'ref'}-${referenceLine.y}-${index}`}
                y={referenceLine.y}
                label={referenceLine.label}
                stroke={referenceLine.color || 'hsl(var(--muted-foreground))'}
                strokeDasharray={referenceLine.strokeDasharray || '3 3'}
                ifOverflow={referenceLine.ifOverflow || 'extendDomain'}
              />
            ))}
          </LineChart>
        </ChartContainer>
      </div>
    </div>
  )
}
